// iter45al — Sentry PII scrubber (frontend/src/lib/sentry.js)
//
// User's rule: "Before live: beforeSend scrubs the config/project object.
// Send a project ID reference, not the full financial model."
//
// This module is the SINGLE gate for what leaves the browser via Sentry.
// It defines an allow-list of safe fields and drops everything else.
// Tested in `src/lib/__tests__/sentry.test.js` — never modify the scrubber
// without updating those tests.
//
// Sentry init is gated on REACT_APP_SENTRY_DSN — unset in dev/preview so
// the DSN is production-only. Absence of the env var is a hard no-op:
// initSentry() returns early without importing @sentry/react, so tree-shake
// still applies for local dev bundles.

// -------- Allow-list contract --------
// Fields we WILL send to Sentry alongside the error:
//   - error.message, error.stack (sanitized by dropWideStrings)
//   - route (window.location.pathname) — no query, no hash
//   - project_id (opaque UUID reference, no data)
//   - run_id (opaque, no data)
//   - locale (en | fr)
//   - component_stack (from React error info; sanitized)
//   - user_id ONLY as opaque hash IF ever wanted later (not sent today)
//
// Everything else is dropped. This includes:
//   input.*, overrides.*, thermal_selection.*, saved_overrides.*,
//   candidates.*, result.*, financials.*, opex.*, capex.*, model.*,
//   email, name, picture, session_token, phone, address, geo,
//   feedback text, engineer-message text, promo codes, any $ values.
const SAFE_KEYS = new Set([
  "project_id", "run_id", "route", "locale", "component_stack",
  "error_message", "error_name", "error_stack",
]);

// Long strings might carry PII (rationale text, buyer notes, address
// blobs). Cap at 400 chars and drop suspiciously long ones. Applies to
// error.stack and component_stack after normalisation.
const MAX_STRING_LEN = 400;
function sanitizeString(s) {
  if (typeof s !== "string") return s;
  const trimmed = s.trim().slice(0, MAX_STRING_LEN);
  // Strip anything that looks like an email or a long number (possible
  // financial figure). Keep the shape but redact the value.
  return trimmed
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "<redacted-email>")
    .replace(/\$?\d[\d,]{5,}(\.\d+)?/g, "<redacted-number>");
}

/**
 * scrubEvent(event) — the beforeSend hook.
 * Returns a NEW event with only allow-listed keys. If the input event
 * is falsy, returns null (drop).
 */
export function scrubEvent(event) {
  if (!event) return null;
  const out = {};
  // Top-level event fields Sentry accepts: exception, message, level,
  // timestamp, tags, contexts, extra, breadcrumbs, user, request.
  out.timestamp = event.timestamp;
  out.level = event.level || "error";
  out.platform = "javascript";
  // Exception — keep type + sanitized message. Stack frames get their
  // filename/lineno/function preserved; vars & context stripped.
  if (event.exception && event.exception.values) {
    out.exception = {
      values: event.exception.values.map((v) => ({
        type: v.type,
        value: sanitizeString(v.value || ""),
        stacktrace: v.stacktrace
          ? { frames: (v.stacktrace.frames || []).map((f) => ({
                filename: f.filename,
                function: f.function,
                lineno: f.lineno, colno: f.colno,
                in_app: f.in_app,
              })) }
          : undefined,
      })),
    };
  } else if (event.message) {
    out.message = sanitizeString(event.message);
  }
  // Contexts — keep only "runtime" and our safe custom context.
  out.contexts = {};
  const safeCtx = (event.contexts && event.contexts.fev) || {};
  const filteredCtx = {};
  for (const k of Object.keys(safeCtx)) {
    if (SAFE_KEYS.has(k) && (typeof safeCtx[k] === "string" ||
                              typeof safeCtx[k] === "number" ||
                              typeof safeCtx[k] === "boolean")) {
      filteredCtx[k] = typeof safeCtx[k] === "string"
        ? sanitizeString(safeCtx[k]) : safeCtx[k];
    }
  }
  if (Object.keys(filteredCtx).length) out.contexts.fev = filteredCtx;
  // Route only from window.location.pathname (no query / no hash).
  out.request = { url: undefined };
  if (event.request && event.request.url) {
    try {
      const u = new URL(event.request.url);
      out.request.url = u.pathname;   // path only, drop search + hash
    } catch { /* ignore */ }
  }
  // Explicitly strip these — Sentry may auto-populate.
  out.user = undefined;
  out.breadcrumbs = undefined;
  out.extra = undefined;
  out.tags = event.tags && SAFE_KEYS.has("project_id") && event.tags.project_id
    ? { project_id: event.tags.project_id } : undefined;
  return out;
}

// -------- Public init --------
let _inited = false;
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn || _inited) return false;   // gated by env — dev/preview is no-op
  // Lazy import so the bundle doesn't ship Sentry for envs that don't use it.
  return import("@sentry/react").then(({ init, browserTracingIntegration }) => {
    init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENV || "production",
      release: process.env.REACT_APP_RELEASE || undefined,
      integrations: [browserTracingIntegration()],
      tracesSampleRate: 0.05,
      sendDefaultPii: false,     // hard off — belt AND braces alongside beforeSend
      beforeSend: scrubEvent,
      beforeBreadcrumb: () => null,   // drop all breadcrumbs (they can leak state)
    });
    _inited = true;
    return true;
  });
}

/**
 * captureError(error, {project_id, run_id, locale, componentStack})
 * ErrorBoundary hook. No-op when Sentry not initialized.
 */
export async function captureError(error, ctx = {}) {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  const { captureException, withScope } = await import("@sentry/react");
  withScope((scope) => {
    // Only allow-listed context flows in.
    const safe = {};
    for (const k of ["project_id", "run_id", "locale"]) {
      if (ctx[k] && typeof ctx[k] === "string" && ctx[k].length < 100) safe[k] = ctx[k];
    }
    if (ctx.componentStack && typeof ctx.componentStack === "string") {
      safe.component_stack = sanitizeString(ctx.componentStack);
    }
    if (typeof window !== "undefined" && window.location) {
      safe.route = window.location.pathname;
    }
    scope.setContext("fev", safe);
    if (safe.project_id) scope.setTag("project_id", safe.project_id);
    captureException(error);
  });
}
